import { DateTime } from "luxon";
import { Maybe } from "purify-ts";
import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import useInterval from "use-interval";
import {
  getCurrentTime,
  getGetCurrentTimeQueryKey,
} from "../../modules/api-docs";

// We don't want to cause the application to re-render whenever we check for the server time
// usually we really only care about the server time when we are close to the deadline, so
// that's the only time we need to re-render.

export type ServerDeadlineStates = "before" | "after" | "loading" | "error";
export const useServerTimeDeadline = (props: {
  until: Maybe<DateTime>;
  enabled: boolean;
}) => {
  // Cases:
  // We don't know the deadline yet because the event response hasn't come back yet.
  // - In this case we should just show a loading spinner.
  // We know the deadline but we don't know the server time yet.
  // - In this case we should use the local time to determine if the deadline is in the future or past.
  // We know the deadline and the server time, and the deadline is in the future.
  // - In this case we should use the server time to determine if the deadline is in the future or past.
  // We know the deadline and the server time, and the deadline is in the past.
  // - In this case we should use the server time to determine if the deadline is in the future or past.
  const [getServerTime, setGetServerTime] = useState(true);
  const [state, setState] = useState<ServerDeadlineStates>("loading");
  const until = useRef(props.until);
  const enabled = useRef(props.enabled);
  enabled.current = props.enabled;
  until.current = props.until;

  useInterval(
    () => {
      until.current
        .ifJust(async (deadline) => {
          const localTime = Date.now();
          // Preliminary check to see if the deadline is in the future or past using local time.
          if (deadline > DateTime.fromMillis(localTime)) {
            setState("before");
          } else {
            setState("after");
          }
          // Fetch the server time and check again to see if the deadline is in the future or past.
          const apiTime = await getCurrentTime();
          if (Math.abs(apiTime - localTime) < 60000) {
            setGetServerTime(false);
          }

          if (deadline > DateTime.fromMillis(apiTime)) {
            setState("before");
          } else {
            setState("after");
          }
        })
        .ifNothing(() => {
          if (enabled.current) {
            setState("error");
          }
        });
    },
    props.until && getServerTime ? 1000 : false,
    true
  );

  return state;
};

const useServerTime = (props?: {
  refreshInterval: number;
}): { serverTime: number; getServerTime: boolean } => {
  const qc = useQueryClient();

  const [getServerTime, setGetServerTime] = useState<boolean>(true);
  const [serverTime, setServerTime] = useState<number>(
    new Date().getUTCMilliseconds()
  );

  useInterval(
    async () => {
      const apiTime = await getCurrentTime();

      const localTime = Date.now();
      if (Math.abs(apiTime - localTime) < 60000) {
        setGetServerTime(false);
      }
      setServerTime(apiTime);
      qc.invalidateQueries(getGetCurrentTimeQueryKey());
    },
    getServerTime ? props?.refreshInterval ?? 1000 : false,
    true
  );
  return { serverTime, getServerTime };
};

export const useLuxonServerTime = (props?: {
  refreshInterval: number;
}): {
  serverTime: DateTime;
  getServerTime: boolean;
} => {
  const { serverTime, getServerTime } = useServerTime({
    refreshInterval: props?.refreshInterval,
  });

  const [retTime, setRetTime] = useState<number>(serverTime);

  useEffect(() => {
    if (getServerTime) {
      setRetTime(serverTime);
    } else {
      let interval = setInterval(() => {
        const localTime = Date.now();
        setRetTime(localTime);
      }, props?.refreshInterval ?? 1000);
      return () => clearInterval(interval);
    }
  }, [getServerTime, serverTime]);

  const luxonServerTime = DateTime.fromMillis(retTime);

  return { serverTime: luxonServerTime, getServerTime };
};

export default useServerTime;
